/* ============================================================
   custom.header-search.js — behaviour for the stitched search
   pill in sections/custom-header.liquid.

   Three jobs.

   1. SWAP THE RESULTS IN, DON'T RELOAD THE PAGE. A normal form
      submit throws away the whole document — header, masthead,
      wordmark and all — and repaints it identically a moment
      later, which is what the white flash between the old page and
      the new one actually is. Instead this fetches the two sections
      of the search template through Shopify's Section Rendering API
      and puts them into #MainContent. The header group and the
      footer are never touched, so the pill the shopper just typed
      into physically stays on screen while the grid underneath it
      changes.

      The fetch starts AT THE SAME TIME as the widening animation,
      not after it, so the 340ms the pill spends growing is 340ms of
      the request already in flight.

      EVERY failure path falls back to a real navigation and says
      why in the console, prefixed [FFG search]. Worst case is
      exactly the behaviour we had before, never a broken page.

   2. EXPAND, THEN SWAP. The pill widens to --ffg-search-w-wide
      (roughly double) and the swap waits for that transition to
      finish, so the two moves read as one gesture. The search page
      also renders the pill wide from Liquid, so a hard reload of a
      /search URL lands in the same state this leaves it in.

   3. KEEP THE STITCHES THE SIZE THEY ARE. The outline's dash
      pattern is authored against pathLength="673" (see
      claude/stitched-outline-recipe.md), which means the browser
      scales every stitch by realPerimeter / 673. Grow the pill and
      every stitch grows with it — at double width they read as long
      basting stitches, not the small hand-sewn ones the brand uses
      everywhere else. syncStitch() rewrites pathLength as the pill
      resizes so that ratio stays exactly where it was at the
      collapsed size. The pill gets longer and MORE stitches appear,
      which is what real sewing does.

   ---- TWO THINGS THAT MADE THIS FAIL SILENTLY, BOTH FIXED ----

   PRESERVED URL PARAMETERS. This asks for sections at a URL it
   builds itself, and the first version built that URL from the form
   action alone — which drops preview_theme_id. On an UNPUBLISHED
   theme that is fatal and invisible: the fetch is answered by
   whatever theme is currently published, that theme has no section
   called "search" or "main", the swap finds nothing to use and
   quietly falls back to a full page load. Everything looks broken
   in preview and fine once published, which is the worst possible
   way round. carryContextParams() copies preview_theme_id and
   friends across, the same way Horizon's own section-renderer keeps
   them by starting from window.location.

   ONE SECTION PER REQUEST. Shopify has two forms of this API: one
   section as HTML via section_id, or several at once as JSON via
   sections. This uses section_id, twice, in parallel — the form
   Horizon itself uses everywhere (assets/section-renderer.js), so
   it is the form known to work on this store rather than the one
   that merely should.
   ============================================================ */
(function () {
  'use strict';

  var WIDE_CLASS = 'is-wide';
  var AUTHORED_PATH_LENGTH = 673;
  var WIDEN_TIMEOUT_MS = 700;
  var DIM_AFTER_MS = 400;

  /* WHY THIS FETCHES A PAGE AND NOT TWO SECTIONS (Devin, 10 Aug: "when you
     type in a search and hit return, the whole browser window goes white").

     It used to ask the Section Rendering API for `section_id=search` and
     `section_id=main`. For a JSON template Shopify wants the FULL section id —
     `template--16467137888302__main` — which contains a per-store template key
     that changes whenever the template file is rewritten. Both requests had
     started answering 404, so every search silently fell back to
     `window.location.href`, and with the tag redirect on top of that a search
     for "cock" cost three full page loads: the current page, /search, then
     /collections/all/s-cock. Three documents, two of them thrown away, and the
     header rebuilt each time — that is the white flash.

     Fetching the destination document sidesteps the id problem completely:
     #MainContent is a fixed id that exists on every template, so the same code
     path serves a search page and a tag-filtered collection. */
  var MAIN_ID = 'MainContent';

  /* Parameters that decide WHICH THEME and which storefront context
     answers a request. Dropping them silently serves the published
     theme instead of the one being previewed. */
  var CONTEXT_PARAMS = ['preview_theme_id', '_fd', 'pb', '_ab', '_ppp'];

  var inFlight = null;
  var tookOverHistory = false;

  function log(message) {
    if (window.console && window.console.info) {
      window.console.info('[FFG search] ' + message);
    }
  }

  function warn(message) {
    if (window.console && window.console.warn) {
      window.console.warn('[FFG search] full page load — ' + message);
    }
  }

  /* -------- the results swap -------- */

  function canSwap() {
    return !!(
      document.getElementById('MainContent') &&
      window.fetch &&
      window.history &&
      window.history.pushState &&
      window.AbortController &&
      window.DOMParser
    );
  }

  function carryContextParams(url) {
    var here = new URL(window.location.href);
    CONTEXT_PARAMS.forEach(function (name) {
      var value = here.searchParams.get(name);
      if (value !== null && !url.searchParams.has(name)) {
        url.searchParams.set(name, value);
      }
    });
    return url;
  }

  /* WHERE A QUERY GOES. The same decision layout/theme.liquid makes on a full
     page load, made here before navigating so the /search hop never happens:
     a word that names a tag becomes a tag-filtered collection sorted by best
     selling, because Shopify's search cannot sort by best selling at all.
     See docs/search-tagging.md. Anything else is an ordinary search. */
  function destinationFor(query, fallback) {
    var tags = window.FFG_SEARCH_TAGS;
    if (!tags) return fallback;

    /* SOME WORDS MEAN A PAGE, NOT A PRODUCT (10 Aug, Devin: "a search for
       shipping or free shipping takes us to the shipping page. same with
       wholesale which right now takes us to search results showing mixed
       cases and xmas pussy soap. (strange!)" — those two match on their
       descriptions). The map lives in layout/theme.liquid beside the tag
       index so the head redirect and this box can never disagree. */
    var pages = window.FFG_SEARCH_PAGES || {};
    var typed = query.toLowerCase().trim();
    if (pages[typed]) {
      return new URL(pages[typed], window.location.origin);
    }

    /* The typed phrase first, then the same two fallbacks layout/theme.liquid
       applies — see the long note there. Dropping a trailing "soap" because
       every product here is one, then a trailing "s" because the tags are
       singular. Order matters: the typed phrase always wins, so a line actually
       named "soap chunks" is never rewritten out of existence. */
    var word = typed;
    var tries = [word];
    var base = word.replace(/\s+soaps?$/, '').trim();
    if (base && base !== word) tries.push(base);
    tries.slice().forEach(function (candidate) {
      if (candidate.charAt(candidate.length - 1) === 's') tries.push(candidate.slice(0, -1));
    });

    var handle = null;
    for (var i = 0; i < tries.length && !handle; i++) handle = tags[tries[i]];
    if (!handle) return fallback;
    return new URL(
      '/collections/all/' + handle + '?sort_by=best-selling',
      window.location.origin
    );
  }

  function fetchDocument(target) {
    if (inFlight) inFlight.abort();
    var controller = new AbortController();
    inFlight = controller;

    return fetch(target.toString(), { signal: controller.signal }).then(function (response) {
      if (!response.ok) throw new Error('the page responded ' + response.status);
      return response.text();
    });
  }

  /* innerHTML and DOMParser both produce inert <script> elements, so
     any script that came down with a section has to be recreated to
     run — this is how results-list.js and the filters get loaded on
     the first search made from a page that had no results grid on
     it. Re-adding a module already in the module map is a no-op, so
     doing this on every swap is safe. */
  function activateScripts(root) {
    var scripts = root.querySelectorAll('script');
    Array.prototype.forEach.call(scripts, function (original) {
      var replacement = document.createElement('script');
      Array.prototype.forEach.call(original.attributes, function (attr) {
        replacement.setAttribute(attr.name, attr.value);
      });
      if (!original.src) replacement.textContent = original.textContent;
      original.parentNode.replaceChild(replacement, original);
    });
  }

  /* Parsing the response and looking #MainContent up by id — rather than
     trusting the string — is what catches the case where some other theme, or
     an error page, answered instead: the id simply is not in there, and we fall
     back to a real navigation with a reason. */
  function applyDocument(html) {
    var main = document.getElementById(MAIN_ID);
    if (!main) return 'no #MainContent to swap into';

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var incoming = parsed.getElementById(MAIN_ID);
    if (!incoming) return 'the response had no #MainContent in it';

    main.innerHTML = incoming.innerHTML;

    /* data-template drives real CSS — the green collection title tag is
       #MainContent[data-template="collection"] — so it has to travel with the
       markup or a swapped-in collection would be styled as a search page. */
    main.dataset.template = incoming.dataset.template || '';

    /* TITLE FIRST, SCRIPTS SECOND. custom-filter-title.liquid ships a script
       that replaces Shopify's `Products – tagged "s-cock"` with the word the
       shopper typed; setting the title after activating scripts put the raw one
       back every time. */
    var title = parsed.querySelector('title');
    if (title) document.title = title.textContent;

    activateScripts(main);

    /* Through the page's real scroll container — Horizon scrolls .page-wrapper,
       not the window, so window.scrollTo here was a no-op and a search made
       from halfway down a page landed halfway down the results. */
    if (window.FFGPageSwap) {
      window.FFGPageSwap.scrollToY(0);
    } else if (window.scrollY > 0) {
      window.scrollTo(0, 0);
    }
    return null;
  }

  /* -------- the pill -------- */

  /* The outline is a rect with ry="50%", so it is a stadium: two
     half-circles of diameter h joined by two straight runs. */
  function stadiumPerimeter(w, h) {
    if (h >= w) return Math.PI * w;
    return 2 * (w - h) + Math.PI * h;
  }

  function setup(form) {
    if (form.dataset.ffgSearchBound) return;
    form.dataset.ffgSearchBound = '1';

    var input = form.querySelector('.ffg-masthead__search-input');
    var outline = form.querySelector('.ffg-stitched__outline');
    var shape = outline ? outline.querySelector('rect') : null;
    if (!input) return;

    function syncStitch() {
      if (!shape) return;

      var box = outline.getBoundingClientRect();
      if (!box.width || !box.height) return;

      /* How far past its resting width the pill currently is. Only
         the input's width changes, and widening a stadium adds
         exactly 2x that to the perimeter, so the collapsed
         perimeter can be recovered arithmetically — which matters
         because on the search page the pill is born wide and never
         has a collapsed state to measure. */
      var base = parseFloat(
        getComputedStyle(form).getPropertyValue('--ffg-search-w-base')
      );
      var grown = base
        ? Math.max(input.getBoundingClientRect().width - base, 0)
        : 0;

      var now = stadiumPerimeter(box.width, box.height);
      var collapsed = now - 2 * grown;
      if (collapsed <= 0) return;

      var len = AUTHORED_PATH_LENGTH * (now / collapsed);
      shape.setAttribute('pathLength', len.toFixed(1));

      /* The same number, handed to CSS. A full revolution of the stitching is
         one pathLength, and pathLength is rewritten here every time the pill
         resizes — so the submit animation has to read it rather than assume
         673, or it turns most of the way round on a narrow pill and about half
         on a wide one. See the ffg-search-sew keyframe in custom-header.liquid. */
      form.style.setProperty('--ffg-stitch-len', len.toFixed(1));
    }

    /* THE CLEAR BUTTON (10 Aug, Devin: "on iphone it's a pain to backspace
       if you want another search"). Liquid renders its first state from the
       pill's server-side value; from here on this keeps it honest — while
       typing, and after a swap rewrites the pill's value without an input
       event (custom.pageswap.js assigns .value directly, so `ffg:navigated`
       is the signal there). Clearing keeps focus in the input: the point is
       to type the NEXT search, not to dismiss the keyboard. */
    var clearButton = form.querySelector('.ffg-masthead__search-clear');
    if (clearButton) {
      var syncClear = function () {
        clearButton.hidden = input.value === '';
      };
      input.addEventListener('input', syncClear);
      document.addEventListener('ffg:navigated', syncClear);
      syncClear();
      clearButton.addEventListener('click', function () {
        input.value = '';
        syncClear();
        input.focus();
      });
    }

    /* THE PLACEHOLDER ANSWERS THE CURSOR (13 Aug, Devin: "when you click
       into the search pill with your cursor the word SEARCH turns into
       200+ FLAVORS"). The focus text rides the section's
       search_placeholder_wide setting — data-placeholder-wide on the
       input, which has been idle since the one-string-everywhere change
       on 10 Aug — so both words stay editable in the theme editor. Blur
       puts the resting word back. */
    var restingPlaceholder = input.placeholder;
    var focusPlaceholder = input.getAttribute('data-placeholder-wide') || '';
    if (focusPlaceholder) {
      input.addEventListener('focus', function () {
        input.placeholder = focusPlaceholder;
      });
      input.addEventListener('blur', function () {
        input.placeholder = restingPlaceholder;
      });
    }

    /* SCROLLING AWAY RELEASES THE PILL (11 Aug, Devin: "clear it by tapping
       the round x... and then scroll down the page. the top bar flashes on
       and off"). The clear button keeps focus so the next search can be
       typed — but a shopper who scrolls instead has moved on, and on iOS
       the still-focused input holds the keyboard while the scroll
       dismisses it, resizing the visual viewport over and over; the bar
       strobes until it settles.

       TOUCHMOVE, NOT SCROLL (12 Aug, Devin: "a click into the search pill
       results in the phone's keyboard pulling up and then immediately
       going away"). The first version listened for scroll — and OPENING
       the keyboard scrolls the page to keep the input visible, so the
       act of focusing the pill blurred the pill. A finger dragging the
       page is the only honest "I've moved on" signal, and a keyboard
       cannot fake one. Draft queries keep focus either way. */
    window.addEventListener(
      'touchmove',
      function () {
        if (document.activeElement === input && input.value === '') {
          input.blur();
        }
      },
      { passive: true, capture: true }
    );

    /* ResizeObserver fires once on observe and then on every frame
       of the width transition, so the stitching stays a constant
       size all the way through rather than stretching and snapping
       back at the end. */
    if (window.ResizeObserver) {
      new ResizeObserver(syncStitch).observe(input);
    } else {
      window.addEventListener('resize', syncStitch);
      syncStitch();
    }

    /* The pill's height depends on the loaded font, and the height
       is half the perimeter maths. */
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(syncStitch);
    }

    function widen() {
      var wasWide = form.classList.contains(WIDE_CLASS);
      var animates =
        !wasWide && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      return new Promise(function (resolve) {
        if (!animates) {
          form.classList.add(WIDE_CLASS);
          return resolve();
        }
        var settled = false;
        function done() {
          if (settled) return;
          settled = true;
          resolve();
        }
        input.addEventListener('transitionend', function (event) {
          if (event.propertyName === 'width') done();
        });
        window.setTimeout(done, WIDEN_TIMEOUT_MS);
        form.classList.add(WIDE_CLASS);
      });
    }

    form.addEventListener('submit', function (event) {
      var query = input.value.trim();
      if (!query) return;
      if (form.dataset.ffgNavigating) return;

      var searchUrl = new URL(
        form.getAttribute('action') || '/search',
        window.location.origin
      );
      searchUrl.searchParams.set('q', query);
      var type = form.querySelector('input[name="type"]');
      if (type && type.value) searchUrl.searchParams.set('type', type.value);

      var target = destinationFor(query, searchUrl);
      carryContextParams(target);

      var swapping = canSwap();
      var alreadyWide = form.classList.contains(WIDE_CLASS);
      var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (!swapping && (alreadyWide || reducedMotion)) {
        warn('this browser is missing fetch, history or DOMParser');
        return;
      }

      event.preventDefault();
      form.dataset.ffgNavigating = '1';

      /* ONE FULL TURN OF THE THREAD ON SUBMIT (Devin, 31 Jul). The pill
         already answers focus with a half turn, in CSS; this is the second
         beat, and it runs while the results are being fetched — the wait and
         the animation are the same 1.4s of screen time rather than two.

         Restarted rather than added: searching twice without leaving the page
         should stitch twice, and a class that is already present fires no
         animation. Removing it, forcing a reflow, adding it back is the only
         reliable replay. */
      form.classList.remove('is-sewing');
      void form.offsetWidth;
      form.classList.add('is-sewing');
      form.addEventListener(
        'animationend',
        function () {
          form.classList.remove('is-sewing');
        },
        { once: true }
      );

      /* Dismiss the mobile keyboard so the results are visible. */
      input.blur();

      var startedAt = Date.now();
      var main = document.getElementById('MainContent');
      var dim = window.setTimeout(function () {
        /* Only if the network is slow enough that the widening
           animation has stopped covering for it. */
        /* BRIGHTNESS, NOT OPACITY (11 Aug, Devin: "a very brief, partially
           opaque green overlay under the header" when searching FROM a
           product page). Opacity made #MainContent translucent, and on a
           phone-width PDP the thing behind it is the BODY — which is the
           buy box's green field there (custom.pdp.css). White pages hid
           the bleed; the green ones announced it. brightness() shifts the
           pixels without letting the backdrop through. */
        /* A 20% WHITE LAYER, NOT A BRIGHTNESS SHIFT (13 Aug, round two —
           Devin on brightness(1.28): "it's washing out colors. it needs to
           just add a layer of white over the stuff you're dimming at 20%").
           brightness() multiplies, which sends saturated colours somewhere
           strange; compositing white at 20% is pixel*0.8 + 0.2, which only
           an SVG transfer function can state — #ffgWhiteWash in
           sections/custom-header.liquid. url() filters don't interpolate,
           so the 160ms transition is gone with the old filter. */
        if (main) {
          main.style.filter = 'url(#ffgWhiteWash)';
        }
      }, DIM_AFTER_MS);

      function undim() {
        window.clearTimeout(dim);
        if (main) main.style.filter = '';
      }

      var failure = null;
      var results = swapping
        ? fetchDocument(target).catch(function (error) {
            failure = error && error.message ? error.message : 'the request failed';
            return null;
          })
        : Promise.resolve(null);

      if (!swapping) failure = 'this browser is missing fetch, history or DOMParser';

      Promise.all([widen(), results]).then(function (settled) {
        var html = settled[1];

        if (html) {
          var problem = applyDocument(html);
          if (!problem) {
            window.history.pushState({ ffgSearch: true, ffgScroll: 0 }, '', target.toString());
            tookOverHistory = true;
            form.dataset.ffgNavigating = '';
            undim();
            log('results swapped in without a page load, ' + (Date.now() - startedAt) + 'ms');
            return;
          }
          failure = problem;
        }

        undim();
        warn(failure || 'unknown reason');
        window.location.href = target.toString();
      });
    });
  }

  /* Once the URL has been changed under the browser's feet, Back and Forward
     have to be answered by somebody. custom.pageswap.js owns navigation now and
     handles any entry marked ffgSearch, so this only reloads when that file is
     absent — two handlers both acting on the same popstate would swap and then
     immediately reload over the top of it. */
  window.addEventListener('popstate', function () {
    if (!tookOverHistory) return;
    if (window.FFGPageSwap) return;
    window.location.reload();
  });

  /* THE WIDE "SEARCH 200+ FLAVORS" SWAP IS GONE (10 Aug, Devin: "the s in
     flavors is being cut off in the search pill. i think we need to make it
     so the word search doesn't show up there"). It dated from 1 Aug; iPad
     Safari rendered the wide string a few px larger than the 186 it was
     measured at on desktop Chrome, and the pill clipped its own placeholder.
     One string everywhere now — the placeholder ATTRIBUTE — which also let
     the desktop resting width come down (custom-header.liquid). The
     data-placeholder-wide attribute stays in the markup, ignored, so an old
     cached copy of this file cannot null-crash on a page without it. */

  function init() {
    Array.prototype.forEach.call(
      document.querySelectorAll('.ffg-masthead__search'),
      setup
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Theme editor re-renders the header group on every setting change. */
  document.addEventListener('shopify:section:load', init);
})();